<template>
  <b-navbar
    :class="fapp + ' with-sidebar'"
    toggleable="xl"
    type="dark"
    variant="light-blue"
    sticky
  >
    <b-container class="p-0" fluid>
      <b-navbar-brand
        v-if="!isShared || (isShared && applications)"
        :to="localePath(`${fapp ? fapp : 'index'}`)"
      >
        <!-- eslint-disable-next-line -->
        <component :is="logoComponent" />

        <div v-if="isStandaloneOffline" class="offline-label">offline</div>
      </b-navbar-brand>

      <b-navbar-brand v-if="isShared && !applications">
        <!-- eslint-disable-next-line -->
        <component :is="logoComponent" />
      </b-navbar-brand>

      <b-navbar-toggle target="nav-collapse" />

      <b-collapse id="nav-collapse" is-nav>
        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto navbar-nav-icons-center">
          <nuxt-link
            v-if="isShared"
            :to="localePath('settings-bibliography')"
            class="mr-1 nav-icon-link"
          >
            <img
              height="52px"
              alt="documenti"
              :src="`${$assetsBase}images/icons/booklibrary.png`"
            />
          </nuxt-link>
          <nuxt-link
            v-if="showItalianCustomerLinks"
            :to="localePath('settings-documents')"
            class="mr-1 nav-icon-link"
          >
            <img
              height="52px"
              alt="documenti"
              :src="`${$assetsBase}images/icons/documenti-${isButtonPath(
                'documents'
              )}@2x.png`"
            />
          </nuxt-link>
          <a
            v-if="showItalianCustomerLinks"
            :href="shopLink ? shopLink.url : ''"
            :disabled="!shopLink"
            class="mr-1 nav-icon-link"
            target="_blank"
          >
            <img
              height="52px"
              alt="shop"
              :src="`${$assetsBase}images/icons/shop-${isButtonPath(
                'shop'
              )}@2x.png`"
            />
          </a>
          <a
            v-if="showItalianCustomerLinks"
            class="mr-1 nav-icon-link"
            target="_blank"
            :href="$academyUrl"
          >
            <img
              height="52px"
              alt="academy"
              :src="`${$assetsBase}images/icons/academy-${isButtonPath(
                'academy'
              )}@2x.png`"
            />
          </a>
          <b-nav-form
            class="search-form d-flex align-items-center"
            @submit.prevent="search"
          >
            <b-form-input
              v-model="searchQuery"
              size="lg"
              id="search-input"
              class="mr-sm-2 input-transparent"
              style="width: 220px"
              :placeholder="$t('app.common.search')"
              :state="!!searchQuery && searchQuery.length < 3 ? false : null"
            />
            <i
              v-if="searchQuery && searchQuery.length > 2"
              class="fa fa-times clear-search"
              @click="searchQuery = ''"
            />
            <b-tooltip
              :show="!!searchQuery && searchQuery.length < 3"
              variant="danger"
              target="search-input"
              placement="bottom"
              triggers=""
            >
              {{ $t('app.common.searchLimit') }}
            </b-tooltip>
          </b-nav-form>

          <b-nav-item-dropdown
            v-if="user"
            right
            class="d-flex align-items-center user-dropdown"
          >
            <!-- Using 'button-content' slot -->
            <template slot="button-content">
              <span v-if="isShared"
                >{{ $t(`app.users.roles.navbar.${user.role}`) }} -&nbsp;</span
              >{{ user.firstName }} {{ user.lastName }}
            </template>
            <b-dropdown-item
              v-if="
                (!isStandalone && !isShared) ||
                (!isStandalone && applications) ||
                (isStandalone && onLine)
              "
              :to="localePath('settings')"
            >
              {{ $t('app.settings.settings') }}
            </b-dropdown-item>
            <b-dropdown-item v-if="forcedOffline" @click="setOnline">
              {{ $t('app.offline.button.accessOnline') }}
            </b-dropdown-item>

            <b-dropdown-item
              v-if="isStandalone && onLine"
              @click="showModalToSelectSyncMethod"
            >
              {{ $t('app.offline.button.sync') }}
            </b-dropdown-item>

            <b-dropdown-item
              v-if="isStandalone && onLine"
              @click="checkForUpdates"
            >
              {{ $t('app.settings.checkForUpdates') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isShared && applications"
              @click="changePassword"
            >
              {{ $t('app.settings.changePassword') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isShared && applications && user && user.role === 'admin'"
              :to="localePath('/config')"
            >
              {{ $t('app.config.title') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isShared && applications && user && user.role === 'admin'"
              @click="moveData"
            >
              {{ $t('app.config.moveData') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="isShared && applications && user && user.role === 'admin'"
              :to="localePath('/users')"
            >
              {{ $t('app.users.title') }}
            </b-dropdown-item>
            <b-dropdown-divider v-if="onLine" />
            <b-dropdown-item v-if="isShared && applications" @click="logout">
              {{ $t('app.common.switchUser') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="!isShared || (isShared && applications)"
              @click="logout"
            >
              {{ $t('app.common.signOut') }}
            </b-dropdown-item>
          </b-nav-item-dropdown>
          <b-nav-item-dropdown
            right
            no-caret
            variant="link"
            toggle-class="notification-bell-toggle"
            menu-class="notification-dropdown position-xl-static position-absolute"
            ref="notification-dropdown"
          >
            <template #button-content>
              <div
                class="notification-bell d-flex align-items-center justify-content-center position-relative"
                v-if="
                  $platform === 'web' || ($platform === 'electron' && onLine)
                "
              >
                <i class="fas fa-bell fa-2x text-white"></i>
                <b-badge
                  v-if="unreadMessagesCount > 0"
                  variant="danger"
                  pill
                  class="position-absolute badge-top-right"
                >
                  {{ unreadMessagesCount }}
                </b-badge>
              </div>
            </template>

            <!-- Notification list/panel -->
            <div class="notification-panel d-flex flex-column p-0">
              <!-- Fixed header with Mark All button -->
              <div
                v-if="unreadMessagesCount > 0"
                class="px-3 py-3 bg-light border-bottom"
              >
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                  :disabled="isImpersonation"
                  @click="markAllMessageAsRead"
                >
                  <i class="fa-regular fa-envelope-open mr-2"></i>
                  <span>{{ $t('app.notifications.mark-all-read') }}</span>
                </button>
              </div>

              <!-- Scrollable notification cards -->
              <div class="notification-list-scroll flex-grow-1">
                <div
                  v-if="messages && messages.length > 0"
                  class="list-group list-group-flush"
                >
                  <div
                    v-for="(message, i) in messages"
                    :key="i"
                    class="list-group-item list-group-item-action d-flex align-items-start p-3 border-0 border-bottom"
                    :class="[getRowBgClass(message), 'notification-item']"
                    @click="notificationClicked(message)"
                  >
                    <!-- Icon with background and unread indicator -->
                    <div class="mr-3 mt-1 position-relative">
                      <div
                        class="notification-icon-circle rounded-circle bg-white d-flex align-items-center justify-content-center"
                        :class="getIconBorderColor(message)"
                      >
                        <i
                          :class="`${getIconClass(
                            message
                          )} text-primary fa-lg notification-icon`"
                        ></i>
                      </div>
                      <!-- Unread blue dot indicator -->
                      <span
                        v-if="isNotificationToRead(message)"
                        class="notification-unread-dot position-absolute rounded-circle bg-primary"
                      ></span>
                    </div>

                    <!-- Content -->
                    <div class="flex-grow-1 min-w-0">
                      <div
                        class="d-flex justify-content-between align-items-start mb-1"
                      >
                        <h6
                          class="mb-0 text-dark"
                          :class="
                            isNotificationToRead(message)
                              ? 'font-weight-bold'
                              : 'font-weight-normal'
                          "
                        >
                          {{ message.title }}
                        </h6>
                      </div>
                      <p class="mb-0 small text-muted">
                        <i class="far fa-clock mr-1"></i
                        >{{ getDateTime(message.sentAt) }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Empty state -->
                <div v-else class="text-center py-5 px-3">
                  <i class="fas fa-bell-slash fa-3x text-muted mb-3"></i>
                  <p class="text-muted mb-0">
                    {{ $t('app.notifications.no-notifications') }}
                  </p>
                </div>
              </div>

              <!-- Fixed footer -->
              <div class="text-center py-3 bg-light border-top">
                <nuxt-link
                  :to="localePath('notifications')"
                  @click.native.stop="closeNotificationDropdown"
                  class="btn btn-link btn-sm text-decoration-none font-weight-semibold"
                >
                  {{ $t('app.notifications.show-all') }}
                  <i class="fas fa-arrow-right ml-1"></i>
                </nuxt-link>
              </div>
            </div>
          </b-nav-item-dropdown>
          <div class="navbar-collapsed-second-row">
            <img
              :src="`${$assetsBase}images/akern-logo.svg`"
              width="128"
              class="ml-3"
              alt="Akern"
            />

            <div
              v-if="typeof needToAcceptForApp === 'string' && !isImpersonation"
              class="card bg-danger text-white ml-4"
            >
              <div
                class="card-body"
                style="padding-top: 0.75rem; padding-bottom: 0.75rem"
              >
                <i class="fas fa-exclamation-circle mr-1" />
                <nuxt-link
                  :to="localePath(`/accept?app=${needToAcceptForApp}`)"
                  class="text-white font-weight-bold"
                >
                  {{ $t('app.gdpr.acceptGdpr') }}
                </nuxt-link>
              </div>
            </div>
            <div
              v-if="showPasswordWarning && !isImpersonation"
              role="button"
              class="card bg-warning text-white ml-4"
              @click="changePassword(true)"
            >
              <div
                class="card-body d-flex align-items-center"
                style="padding-top: 0.75rem; padding-bottom: 0.75rem"
              >
                <i class="fas fa-exclamation-circle mr-2" />
                <span class="font-weight-bold">{{
                  $t('app.settings.passwordChangeWarningShort')
                }}</span>
              </div>
            </div>
            <div
              v-if="!hasAllFields && onLine && !isImpersonation"
              class="card bg-warning text-white ml-4"
            >
              <div
                role="button"
                @click="openNewFieldsDialog()"
                class="card-body d-flex"
                style="padding-top: 0.75rem; padding-bottom: 0.75rem"
              >
                <div>
                  <i class="fas fa-exclamation-circle mr-1" />
                </div>
                <div class="text-white font-weight-bold">
                  {{ $t('app.common.missingFields') }}
                </div>
              </div>
            </div>
          </div>
        </b-navbar-nav>
      </b-collapse>
    </b-container>

    <ChangePasswordModal
      ref="change-password-modal"
      :super-log="user ? user.superLog : false"
      @dismiss="dismissPasswordWarning"
    />
    <ModalForSelectingSyncMethod ref="modal-for-selecting-sync-method" />
    <MoveDataModal ref="move-data-modal" @done="onDataMoved" />
    <LicenseExpiredAdviseModal ref="license-expired-modal" />
    <add-new-fields
      v-if="showAddNewFieldsModal && user && onLine"
      @updated="checkNewFields"
      @hidden="showAddNewFieldsModal = false"
      :fields="newFields"
      :user="user"
      :has-serial-number="hasSerialNumber"
    ></add-new-fields>

    <!-- Notification Preview dialog -->
    <NotificationPreviewDialog
      ref="notification-preview-modal"
      :message="selectedMessage"
      @openDetail="openDetailModal"
    />

    <!-- Notification Detail dialog -->
    <NotificationDetailDialog
      ref="notification-detail-modal"
      :message="selectedMessage"
    />

    <!-- Alert Critical notification -->
    <AlertNotification
      ref="critical-notification-alert"
      :critical-messages="criticalMessages"
    />
  </b-navbar>
</template>

<script>
/* eslint vue/no-unused-components: 0 */
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ChangePasswordModal from '../components/settings/ChangePasswordModal'
import ModalForSelectingSyncMethod from '../components/ModalForSelectingSyncMethod'
import MoveDataModal from '../components/MoveDataModal'
import LicenseExpiredAdviseModal from '../components/modals/LicenseExpiredAdviseModal'
import bodygramLogo from '~/components/logo.vue'
import hboLogo from '~/components/hboLogo.vue'
import fitnessLogo from '~/components/fitness/logo.vue'
import regionalLogo from '~/components/regional/logo.vue'
import ketoLogo from '~/components/keto/logo.vue'
import pentaLogo from '~/components/keto/pentaLogo.vue'
import addNewFields from '~/components/modals/addNewFields'
import NotificationPreviewDialog from '~/components/modals/NotificationPreviewDialog'
import NotificationDetailDialog from '~/components/modals/NotificationDetailDialog'
import AlertNotification from '~/components/modals/AlertNotification'
import notificationsMixin from '~/mixins/notificationsMixin.js'

export default {
  components: {
    bodygramLogo,
    hboLogo,
    fitnessLogo,
    regionalLogo,
    ChangePasswordModal,
    ketoLogo,
    pentaLogo,
    ModalForSelectingSyncMethod,
    MoveDataModal,
    LicenseExpiredAdviseModal,
    addNewFields,
    NotificationPreviewDialog,
    NotificationDetailDialog,
    AlertNotification,
  },
  mixins: [notificationsMixin],
  props: {
    fapp: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      searchQuery: '',
      showAddNewFieldsModal: false,
      passwordWarningDismissedAt: null,
      newFields: [
        'vatNumber',
        'taxCode',
        'state',
        'city',
        'cap',
        'address',
        'occupation',
        'field',
      ],
      selectedMessage: null,
      criticalMessages: [],
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/user',
      onLine: 'network/onLine',
      forcedOffline: 'network/forcedOffline',
      settings: 'user/settings',
      isShared: 'utils/isShared',
      isStandalone: 'utils/isStandalone',
      isStandaloneOffline: 'network/isStandaloneOffline',
      isLocalBackend: 'network/isLocalBackend',
      bodygramApplication: 'applications/bodygram',
      fitnessApplication: 'applications/fitness',
      regionalApplication: 'applications/regional',
      shopLink: 'dashboard/shopLink',
      applications: 'applications/list',
      accessToken: 'user/token',
      messages: 'messages/messages',
      unreadMessagesCount: 'messages/unreadCount',
      isPasswordChangeRequired: 'user/isPasswordChangeRequired',
      isImpersonation: 'user/isImpersonation',
    }),
    // Documents, shop and academy are remote resources: hidden on HBO and on
    // the offline desktop app
    showItalianCustomerLinks() {
      return this.user?.customer?.country === 'it' && !this.isLocalBackend
    },
    hasSerialNumber() {
      if (!this.user?.customer?.devices) return false
      const regex = new RegExp('20(0[1-9]|[1-9][0-9])(0[1-9]|1[0-2])[0-9]{3}$')
      return this.user.customer.devices.some(
        (item) =>
          regex.test(item.serialNumber) && item.serialNumber?.length >= 10
      )
    },
    hasAllFields() {
      if (!this.user?.customer) return true
      const customer = this.user.customer
      if (customer.country !== 'it') return true
      return (
        this.newFields.reduce((acc, key) => {
          if (key === 'vatNumber')
            return acc && (customer.hasVatNumber ? !!customer[key] : true)
          return acc && !!customer[key]
        }, true) && this.hasSerialNumber
      )
    },
    showPasswordWarning() {
      if (!this.onLine || this.isShared || !this.isPasswordChangeRequired)
        return false
      const dismissed = this.passwordWarningDismissedAt
      if (!dismissed) return true
      return this.$dayjs().diff(this.$dayjs(dismissed), 'hour') >= 24
    },
    availableLocales() {
      return this.$i18n.locales
    },
    logoComponent() {
      if (this.fapp) {
        return this.fapp === 'keto' &&
          this.applications.find((x) => x.code === 'KETP_TMP').licensed
          ? 'pentaLogo'
          : this.fapp + 'Logo'
      }

      if (this.isShared) {
        return 'hboLogo'
      }

      return 'bodygramLogo'
    },
    needToAcceptForApp() {
      if (this.$route.name.startsWith('fitness')) {
        if (
          this.fitnessApplication &&
          this.fitnessApplication.signature &&
          this.fitnessApplication.signature.signatureRequired
        ) {
          return 'FIT'
        }
      } else if (this.$route.name.startsWith('regional')) {
        if (
          this.regionalApplication &&
          this.regionalApplication.signature &&
          this.regionalApplication.signature.signatureRequired
        ) {
          return 'REG'
        }
      } else if (
        this.bodygramApplication &&
        this.bodygramApplication.signature &&
        this.bodygramApplication.signature.signatureRequired
      ) {
        return 'BOD'
      }

      return null
    },
  },
  watch: {
    '$route.name': {
      handler(newRouteName, oldRouteName) {
        const targetRoutes = ['index']

        let baseRouteName = newRouteName.split('-').pop()

        // Nuxt routesNameSeparator - default: '___'
        if (baseRouteName.includes('___')) {
          baseRouteName = baseRouteName.split('___')[0]
        } else if (newRouteName.includes('___')) {
          baseRouteName = newRouteName.split('___')[0]
        }

        if (this.isLocalBackend) return

        // Get messages when navigation to targetRoutes
        if (targetRoutes.includes(baseRouteName)) {
          this.getAndShowMessages()
        }
      },
    },
  },
  created() {
    if (this.isLocalBackend) return
    this.getAndShowMessages()
  },
  mounted() {
    const dismissed = localStorage.getItem('passwordChangeWarningDismissedAt')
    if (dismissed) this.passwordWarningDismissedAt = parseInt(dismissed, 10)
    if (this.onLine) {
      // A cached user may predate the devices-in-signin fix and miss
      // customer.devices: refetch it only when the missing-fields alert
      // would show, so healthy users keep the cached user with no extra call.
      if (!this.hasAllFields) {
        this.invalidateUser()
      }
      this.checkNewFields()
    }
    this.getDashboard()
    this.handleExpiredLicense()
  },
  methods: {
    ...mapActions({
      logout: 'user/logout',
      fetchUser: 'user/fetchUser',
      invalidateUser: 'user/invalidateUser',
      searchPatients: 'patients/searchPatients',
      getDashboard: 'dashboard/getDashboard',
    }),
    ...mapMutations({
      setStatus: 'network/setStatus',
    }),
    openNewFieldsDialog() {
      if (!this.user || !this.onLine) {
        return
      }
      this.showAddNewFieldsModal = true
      this.$nextTick(() => {
        this.$bvModal.show('add-new-fields')
      })
    },
    setOnline() {
      this.setStatus(true)
      this.$router.push(
        this.localePath({
          name: 'index',
        })
      )
    },
    isButtonPath(button) {
      return this.$route.name.includes(button) ? 'ciano' : 'bianco'
    },
    showModalToSelectSyncMethod() {
      this.$refs['modal-for-selecting-sync-method'].showModal()
    },
    search() {
      if (this.searchQuery && this.searchQuery.length > 2) {
        if (this.$route.name.includes('search')) {
          this.searchPatients(this.searchQuery)
        }
        this.$router.push({
          path: this.localePath('search'),
          query: { q: this.searchQuery },
        })
      }
    },
    checkForUpdates() {
      this.$ipc('checkForUpdates', {
        silent: false,
        locale: this.$i18n.localeProperties.code,
      })
    },
    changePassword(fromPasswordWarning) {
      this.$refs['change-password-modal'].showModal(
        fromPasswordWarning === true
      )
    },
    moveData() {
      this.$refs['move-data-modal'].showModal()
    },
    handleExpiredLicense() {
      if (this.isShared) {
        this.$refs['license-expired-modal'].showModal()
      }
    },
    checkNewFields() {
      this.fetchUser()
    },
    dismissPasswordWarning() {
      const at = Date.now()
      this.passwordWarningDismissedAt = at
      localStorage.setItem('passwordChangeWarningDismissedAt', String(at))
    },
    onDataMoved() {
      window.location.reload()
    },
    openNotificationCenter() {
      this.$bvToast.toast('Apri centro notifiche', {
        title: 'Notifiche',
        variant: 'info',
        solid: true,
      })
    },
    async markAllMessageAsRead() {
      await this.$store.dispatch('messages/markAllMessagesAsRead')
    },
    getDateTime(date) {
      return this.$dayjs.utc(date).local().format('L, LT')
    },
    notificationClicked(message) {
      if (this.isCriticalNotification(message) || !message?.hasPreview) {
        if (!this.isNotificationToRead(message)) return

        this.markThisMessageAsRead(message)
        return
      }

      if (message.hasPreview) {
        this.showMessagePreview(message)
      }
    },
    showAlertNotifications() {
      this.$refs['critical-notification-alert'].showModal()
    },
    async getAndShowMessages() {
      if (this.isLocalBackend) return

      await this.$store.dispatch('messages/getEssentialMessages')

      this.criticalMessages = this.messages.filter(
        (msg) =>
          this.isCriticalNotification(msg) && !this.isNotificationExpired(msg)
      )

      const nonCriticalToReadMessages = this.messages.filter(
        (msg) =>
          !this.isCriticalNotification(msg) &&
          this.isNotificationToRead(msg) &&
          !this.isNotificationExpired(msg) &&
          msg.hasPreview
      )
      const nonCriticalMessagesToBeAccepted = nonCriticalToReadMessages.filter(
        (msg) => this.isNotificationToBeAccepted(msg)
      )

      if (this.criticalMessages?.length > 0) {
        this.showAlertNotifications()
      }

      let messageToShow = null

      if (nonCriticalMessagesToBeAccepted?.length > 0) {
        messageToShow = nonCriticalMessagesToBeAccepted[0]
      } else if (nonCriticalToReadMessages?.length > 0) {
        messageToShow = nonCriticalToReadMessages[0]
      }

      this.showMessagePreview(messageToShow)
    },
    showMessagePreview(messageToShow) {
      if (!messageToShow) return

      this.selectedMessage = messageToShow
      this.$refs['notification-preview-modal'].showModal()
    },
    closeNotificationDropdown() {
      if (this.$refs['notification-dropdown']) {
        this.$refs['notification-dropdown'].hide()
      }
    },
  },
}
</script>

<style lang="scss">
.navbar-nav-icons-center {
  align-items: center;
}

.nav-icon-link {
  display: flex;
  align-items: center;
}

.user-dropdown {
  ::v-deep .nav-link {
    height: 57px;
    display: flex;
    align-items: center;
    padding: 0rem;
  }
}
.notification-bell-toggle {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

.notification-bell-toggle:focus,
.notification-bell-toggle:active,
.notification-bell-toggle.show {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

.notification-bell {
  width: 40px;
  height: 40px;
  cursor: pointer;
}

.badge-top-right {
  top: 4px;
  right: 2px;
  transform: translate(50%, -50%);
  font-size: 0.75rem;
  padding: 0.4em 0.5em;
}

.notification-dropdown {
  width: 380px;
  max-width: 95vw;
  padding: 0;
  border-radius: 8px;
  overflow: visible;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 12px;
}

.notification-dropdown::before {
  content: '';
  position: absolute;
  top: -12px;
  right: 15px;
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-bottom: 12px solid white;
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
}

.notification-panel {
  overflow: hidden;
  border-radius: 8px;
}

.notification-list-scroll {
  max-height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
}

.notification-icon-circle {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border: 2px solid;
}

.notification-icon {
  line-height: 1;
}

.notification-unread-dot {
  width: 12px;
  height: 12px;
  top: -2px;
  right: -2px;
  border: 2px solid white;
}

.notification-item {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.notification-item:hover {
  background-color: #f8f9fa !important;
  transform: translateX(2px);
}

#nav-collapse.navbar-collapse {
  transition: none !important;
}

@media (max-width: 1199px) {
  #nav-collapse.navbar-collapse {
    overflow: hidden;
  }

  #nav-collapse.navbar-collapse.show {
    display: flex !important;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }

  #nav-collapse.navbar-collapse.show .navbar-nav.navbar-nav-icons-center {
    flex-direction: row !important;
    flex-wrap: wrap;
    align-items: center;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
  }

  #nav-collapse.navbar-collapse.show .navbar-collapsed-second-row {
    width: 100%;
    align-items: center;
    min-width: 0;
  }

  .navbar-collapsed-second-row > img {
    flex-shrink: 0;
  }

  .nav-icon-link img {
    height: 36px !important;
  }

  .search-form .form-control,
  .search-form input#search-input {
    width: 140px !important;
    max-width: 140px;
  }

  .user-dropdown ::v-deep .nav-link {
    height: auto;
    padding: 0.25rem 0.5rem;
    font-size: 0.9rem;
  }

  .navbar.with-sidebar img.ml-3[alt='Akern'] {
    width: 96px;
    height: auto;
  }

  .notification-dropdown {
    left: 0 !important;
  }

  .notification-dropdown::before {
    right: auto;
    left: 7px;
  }
}

.navbar-collapsed-second-row {
  display: contents;
}
</style>
